var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Autocomplete, Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { RoleCategories } from '../../../application/common/constants/role-categories';
import { useGetAllCompaniesQuery } from '../../../application/features/companies/company-api';
import { useCreateFaqMutation } from '../../../application/features/faqs/faq-api';
import { useLazyGetAllMachinesByCompanyQuery } from '../../../application/features/machines/machine-api';
import { useAppSelector } from '../../../application/store/useStore';
import useTranslate from '../../../localization/helpers';
import TextEditor from '../../components/TextEditor';
var CreateFaq = function (_a) {
    var isOpen = _a.isOpen, onClose = _a.onClose;
    var theme = useTheme();
    var _b = useTranslate(), translate = _b.translate, tWithField = _b.tWithField, tWithFunction = _b.tWithFunction;
    var currentCompany = useAppSelector(function (state) { return state.common; }).currentCompany;
    var currentUser = useAppSelector(function (state) { return state.auth; }).currentUser;
    var _c = useGetAllCompaniesQuery({
        filterByCompany: true
    }), _d = _c.data, companies = _d === void 0 ? [] : _d, companiesFetching = _c.isFetching;
    var _e = __read(useCreateFaqMutation(), 2), createFaq = _e[0], _f = _e[1], isLoading = _f.isLoading, isSuccess = _f.isSuccess;
    var isSuperAdmin = currentUser.roleCategory === RoleCategories.SuperAdmin;
    var _g = useForm({
        defaultValues: {
            companyId: '',
            machineId: '',
            faqs: [
                {
                    question: '',
                    answer: ''
                }
            ]
        }
    }), control = _g.control, handleSubmit = _g.handleSubmit, watch = _g.watch, setValue = _g.setValue, errors = _g.formState.errors;
    var _h = useFieldArray({
        name: 'faqs',
        control: control
    }), fields = _h.fields, append = _h.append, remove = _h.remove;
    var handleAddFaq = function () {
        var faqCreationData = {
            question: '',
            answer: ''
        };
        append(faqCreationData);
    };
    var selectedCompany = watch('companyId');
    var _j = __read(useLazyGetAllMachinesByCompanyQuery(), 2), triggerGetMachines = _j[0], _k = _j[1], _l = _k.data, machines = _l === void 0 ? [] : _l, machinesFetching = _k.isFetching;
    useEffect(function () {
        if (!isSuperAdmin)
            setValue('companyId', currentCompany.id);
    }, [currentUser, currentCompany, isSuperAdmin, setValue]);
    useEffect(function () {
        if (selectedCompany) {
            triggerGetMachines({
                companyId: selectedCompany,
                isMachineLibrary: false
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany]);
    useEffect(function () {
        if (isSuccess) {
            enqueueSnackbar(tWithField('toolTips.createSuccess', 'common.faq'), {
                autoHideDuration: 3000,
                variant: 'success',
                anchorOrigin: { horizontal: 'right', vertical: 'top' }
            });
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);
    var onSubmit = function (faq) { return createFaq(faq); };
    var filteredMachines = useMemo(function () {
        var uniqueMachineNames = new Set();
        return machines
            .filter(function (machine) {
            var match = machine.name.match(/^(.*?)\s\(/);
            var machineName = match ? match[1] : machine.name;
            var isUnique = !uniqueMachineNames.has(machineName);
            if (isUnique) {
                uniqueMachineNames.add(machineName);
            }
            return isUnique;
        })
            .map(function (machine) {
            var match = machine.name.match(/^(.*?)\s\(/);
            var machineName = match ? match[1] : machine.name;
            return __assign(__assign({}, machine), { name: machineName });
        })
            .sort(function (a, b) { return a.name.localeCompare(b.name); });
    }, [machines]);
    var validateAnswer = function (value) {
        var _a;
        var div = document.createElement('div');
        div.innerHTML = value;
        var isEmpty = !((_a = div.textContent) === null || _a === void 0 ? void 0 : _a.trim());
        return !isEmpty || tWithField('validations.fieldIsRequired', 'faqs.answer');
    };
    return (_jsxs(Dialog, { sx: { borderRadius: '44px' }, fullWidth: true, scroll: 'paper', open: isOpen, maxWidth: 'sm', children: [_jsxs(DialogTitle, { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '20px'
                }, children: [_jsx(Typography, { variant: 'h6', children: tWithFunction('actions.addFunction', 'common.faqs') }), _jsx(IconButton, { color: 'inherit', style: { marginLeft: 'auto' }, onClick: onClose, children: _jsx(Icon, { icon: 'mingcute:close-line', fontSize: 20, color: '#637381' }) })] }), _jsx(DialogContent, { dividers: true, children: _jsx(Box, { component: 'form', noValidate: true, autoComplete: 'off', children: _jsxs(Grid, { container: true, spacing: 3, p: 1, children: [isSuperAdmin && (_jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { control: control, name: 'companyId', rules: {
                                        required: {
                                            value: true,
                                            message: tWithField('validations.fieldIsRequired', 'common.company')
                                        }
                                    }, render: function (_a) {
                                        var _b, _c;
                                        var _d = _a.field, value = _d.value, onChange = _d.onChange;
                                        return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(Autocomplete, { noOptionsText: translate('common.noOptions'), size: 'medium', sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                                                                padding: '3.5px 4px 3.5px 8px'
                                                            }
                                                        }
                                                    }, options: companies.filter(function (company) { return company.isActive === true; }), value: (_b = companies.find(function (c) { return c.id === value; })) !== null && _b !== void 0 ? _b : null, loading: companiesFetching, isOptionEqualToValue: function (option, value) { return option.id === value.id; }, getOptionLabel: function (option) { return option.name; }, renderInput: function (params) { return (_jsx(TextField, __assign({ error: !!errors.companyId }, params, { variant: 'outlined', fullWidth: true, label: translate('common.company') }))); }, onChange: function (_, value) {
                                                        setValue('machineId', '');
                                                        onChange(value === null || value === void 0 ? void 0 : value.id);
                                                    } }), _jsx(FormHelperText, { error: true, children: (_c = errors.companyId) === null || _c === void 0 ? void 0 : _c.message })] }));
                                    } }) })), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Controller, { control: control, name: 'machineId', rules: {
                                        required: {
                                            value: true,
                                            message: tWithField('validations.fieldIsRequired', 'common.asset')
                                        }
                                    }, render: function (_a) {
                                        var _b, _c;
                                        var _d = _a.field, value = _d.value, onChange = _d.onChange;
                                        return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(Autocomplete, { noOptionsText: translate('common.noOptions'), size: 'medium', sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                                                                padding: '3.5px 4px 3.5px 8px'
                                                            }
                                                        }
                                                    }, options: filteredMachines.filter(function (machine) { return machine.isActive; }), value: (_b = filteredMachines.find(function (c) { return c.id === value; })) !== null && _b !== void 0 ? _b : null, loading: machinesFetching, isOptionEqualToValue: function (option, value) { return option.id === value.id; }, getOptionLabel: function (option) { return option.name; }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { error: !!errors.machineId, variant: 'outlined', fullWidth: true, label: translate('common.asset') }))); }, onChange: function (_, value) {
                                                        onChange(value === null || value === void 0 ? void 0 : value.id);
                                                    } }), _jsx(FormHelperText, { error: true, children: (_c = errors.machineId) === null || _c === void 0 ? void 0 : _c.message })] }));
                                    } }) }), _jsx(Grid, { size: { xs: 12, md: 12, sm: 12, lg: 12 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '20px', children: [fields.map(function (field, index) { return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, p: '8px 16px', boxShadow: theme.customShadows.z8, position: 'relative', borderRadius: '12px', children: [_jsx(Badge, { overlap: 'circular', sx: {
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        zIndex: 10,
                                                        '& .MuiBadge-badge': {
                                                            backgroundColor: '#E4E6EB',
                                                            color: '#161C24'
                                                        }
                                                    } }), fields.length > 1 && (_jsx(IconButton, { style: {
                                                        position: 'absolute',
                                                        borderRadius: '500px',
                                                        top: -18,
                                                        right: -16,
                                                        zIndex: 10,
                                                        backgroundColor: 'rgba(255, 86, 48, 0.08)',
                                                        color: theme.palette.error.main
                                                    }, size: 'small', onClick: function () { return remove(index); }, children: _jsx(Icon, { icon: 'mingcute:close-line' }) })), _jsx(Controller, { control: control, name: "faqs.".concat(index, ".question"), rules: {
                                                        required: {
                                                            value: true,
                                                            message: tWithField('validations.fieldIsRequired', 'faqs.question')
                                                        },
                                                        maxLength: {
                                                            value: 512,
                                                            message: translate('validations.maximumCharactersAllowed', { count: 512 })
                                                        },
                                                        validate: function (value) {
                                                            return value.trim() !== '' ||
                                                                tWithField('validations.fieldIsRequired', 'faqs.question');
                                                        }
                                                    }, render: function (_a) {
                                                        var _b, _c, _d, _e, _f, _g;
                                                        var _h = _a.field, onChange = _h.onChange, value = _h.value;
                                                        return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(TextField, { onChange: onChange, value: value, error: !!((_d = (_c = (_b = errors === null || errors === void 0 ? void 0 : errors.faqs) === null || _b === void 0 ? void 0 : _b[index]) === null || _c === void 0 ? void 0 : _c.question) === null || _d === void 0 ? void 0 : _d.message), slotProps: { input: { sx: { borderRadius: ' 12px' } } }, multiline: true, minRows: 4, fullWidth: true, label: translate('faqs.question'), variant: 'outlined' }), _jsx(FormHelperText, { error: true, children: (_g = (_f = (_e = errors === null || errors === void 0 ? void 0 : errors.faqs) === null || _e === void 0 ? void 0 : _e[index]) === null || _f === void 0 ? void 0 : _f.question) === null || _g === void 0 ? void 0 : _g.message })] }));
                                                    } }), _jsx(Grid, { size: { xs: 12, md: 12, sm: 12, lg: 12 }, children: _jsx(Controller, { control: control, name: "faqs.".concat(index, ".answer"), rules: {
                                                            required: {
                                                                value: true,
                                                                message: tWithField('validations.fieldIsRequired', 'faqs.answer')
                                                            },
                                                            maxLength: {
                                                                value: 5120,
                                                                message: translate('validations.maximumCharactersAllowed', {
                                                                    count: 5120
                                                                })
                                                            },
                                                            validate: validateAnswer
                                                        }, render: function (_a) {
                                                            var _b, _c, _d;
                                                            var _e = _a.field, onChange = _e.onChange, value = _e.value;
                                                            return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(TextEditor, { value: value, onChange: onChange, placeholder: translate('faqs.answer') }), _jsx(FormHelperText, { error: true, children: (_d = (_c = (_b = errors === null || errors === void 0 ? void 0 : errors.faqs) === null || _b === void 0 ? void 0 : _b[index]) === null || _c === void 0 ? void 0 : _c.answer) === null || _d === void 0 ? void 0 : _d.message })] }));
                                                        } }) })] }, field.id)); }), _jsx(Box, { display: 'flex', justifyContent: 'flex-end', children: _jsx(Button, { variant: 'outlined', color: 'primary', size: 'medium', onClick: function () { return handleAddFaq(); }, children: translate('actions.add') }) })] }) })] }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { disabled: isLoading, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'medium', children: translate('actions.cancel') }), _jsx(Button, { disabled: isLoading, onClick: handleSubmit(onSubmit), color: 'inherit', variant: 'contained', size: 'medium', children: tWithFunction('actions.addFunction', 'common.faq') })] })] }));
};
export default CreateFaq;
